import { Component } from '@theme/component';

/**
 * @typedef {Object} EmailSignupFormRefs
 * @property {HTMLInputElement} [tagsInput] - Hidden `contact[tags]` input submitted to Shopify.
 * @property {HTMLInputElement[]} [options] - The subscription preference checkboxes.
 * @property {HTMLElement} [preferencesError] - Container for the "select at least one" message.
 * @property {HTMLInputElement} [fullName] - Single name input, when the block collects a full name.
 * @property {HTMLInputElement} [lastName] - Hidden `contact[last_name]` input paired with `fullName`.
 * @property {HTMLInputElement} [birthday] - The birthday date input.
 */

/**
 * Adds the fields Shopify's customer form has no native slot for.
 *
 * Shopify's `customer` form only persists email, first name, last name and tags, so subscription
 * preferences and birthdays are folded into the single `contact[tags]` field. A full name is split
 * across the first and last name fields on submit.
 *
 * Without JavaScript the form still submits: the hidden tags input carries the preferences that are
 * checked by default, and a full name lands in the first name field.
 *
 * @extends {Component<EmailSignupFormRefs>}
 */
class EmailSignupForm extends Component {
  /** @type {HTMLFormElement | null} */
  #form = null;

  connectedCallback() {
    super.connectedCallback();

    this.#form = this.closest('form');
    this.addEventListener('change', this.#handleChange);
    this.#form?.addEventListener('submit', this.#handleSubmit);
    this.#syncTags();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('change', this.#handleChange);
    this.#form?.removeEventListener('submit', this.#handleSubmit);
    this.#form = null;
  }

  /** @returns {HTMLInputElement[]} The checked preference checkboxes. */
  get #selected() {
    return (this.refs.options ?? []).filter((option) => option.checked);
  }

  /** @returns {boolean} Whether at least one preference must be selected. */
  get #preferencesRequired() {
    return this.hasAttribute('data-preferences-required');
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    this.#syncTags();

    if (event.target.type === 'checkbox' && this.#selected.length > 0) {
      this.#togglePreferencesError(false);
    }
  };

  /** @param {Event} event */
  #handleSubmit = (event) => {
    this.#syncTags();

    if (this.#preferencesRequired && (this.refs.options ?? []).length > 0 && this.#selected.length === 0) {
      event.preventDefault();
      // Shopify's captcha script listens for `submit` on the document and re-submits the form
      // itself, so the event has to stop here or it posts anyway.
      event.stopPropagation();
      this.#togglePreferencesError(true);
      this.refs.options?.[0]?.focus();
      return;
    }

    this.#splitFullName();
  };

  /**
   * Writes the base tags, every selected preference and the birthday into the hidden tags input.
   */
  #syncTags() {
    const { tagsInput } = this.refs;

    if (!tagsInput) return;

    const base = (this.dataset.baseTags ?? '').split(',');
    const selected = this.#selected.map((option) => option.value);
    const birthday = this.#birthdayTag();
    const tags = [...base, ...selected, birthday].map((tag) => tag.trim()).filter(Boolean);

    tagsInput.value = [...new Set(tags)].join(', ');
  }

  /**
   * Builds the birthday tag from the date input, e.g. `birthday-05-12`.
   *
   * @returns {string} The tag, or an empty string when there is no usable date.
   */
  #birthdayTag() {
    const { birthday } = this.refs;
    const prefix = this.dataset.birthdayTagPrefix;

    // `value` is always `YYYY-MM-DD` for a date input, regardless of the displayed locale format.
    if (!birthday?.value || !prefix) return '';

    const [year, month, day] = birthday.value.split('-');

    if (!year || !month || !day) return '';

    return this.dataset.birthdayTagFormat === 'full_date'
      ? `${prefix}${year}-${month}-${day}`
      : `${prefix}${month}-${day}`;
  }

  /**
   * Moves everything after the first word of a full name into the hidden last name field, so the
   * customer record gets both parts.
   */
  #splitFullName() {
    const { fullName, lastName } = this.refs;

    if (!fullName || !lastName) return;

    const parts = fullName.value.trim().split(/\s+/);

    if (parts.length < 2) return;

    fullName.value = /** @type {string} */ (parts.shift());
    lastName.value = parts.join(' ');
  }

  /** @param {boolean} visible */
  #togglePreferencesError(visible) {
    const { preferencesError } = this.refs;

    if (!preferencesError) return;

    preferencesError.hidden = !visible;
  }
}

if (!customElements.get('email-signup-form')) customElements.define('email-signup-form', EmailSignupForm);
